import { FormEvent, useCallback, useMemo, useState } from 'react';
import PageHeader from './components/PageHeader';
import SortableTableHeader from './components/SortableTableHeader';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import {
  manuallyVerifyUsers as manuallyVerifyUsersRequest,
  sendUserPasswordReset as sendUserPasswordResetRequest,
  searchUsersByEmail,
} from './hooks/useUsers';
import { getNextSortCriterion, type FieldSortCriterionValue } from './utils/tableSort';
import { toTestIdPart } from './utils/testIds';

const ORG_PREVIEW_COUNT = 5;

function OrganizationsCell({ organizations, testIdPrefix }: { organizations: string[]; testIdPrefix?: string }) {
  const [expanded, setExpanded] = useState(false);

  if (!organizations?.length) {
    return <span data-testid={testIdPrefix ? `${testIdPrefix}-orgs-empty` : undefined}>-</span>;
  }

  // A user can hold several roles in one organization, which repeats the name verbatim.
  const uniqueOrganizations = Array.from(new Set(organizations));
  const total = uniqueOrganizations.length;
  const isOverflowing = total > ORG_PREVIEW_COUNT;
  const visible = expanded || !isOverflowing ? uniqueOrganizations : uniqueOrganizations.slice(0, ORG_PREVIEW_COUNT);

  return (
    <>
      <div className="org-chip-list" data-testid={testIdPrefix ? `${testIdPrefix}-orgs` : undefined}>
        {visible.map((org) => (
          <span
            key={org}
            className="org-chip"
            title={org}
            data-testid={testIdPrefix ? `${testIdPrefix}-org-${toTestIdPart(org, 'unnamed')}` : undefined}
          >
            {org}
          </span>
        ))}
      </div>
      {isOverflowing && (
        <button
          type="button"
          className="org-chip-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Show fewer organizations' : `Show all ${total} organizations`}
          data-testid={testIdPrefix ? `${testIdPrefix}-orgs-toggle` : undefined}
        >
          {expanded ? 'Show less' : `Show all (${total})`}
        </button>
      )}
    </>
  );
}

interface IUserSearchResult {
  id: number;
  email: string;
  name: string;
  organizations?: string[];
  verifiedEmail: boolean;
}

function getUserSortValue(user: IUserSearchResult, fieldName: string) {
  if (fieldName === 'email') return user.email || '';
  if (fieldName === 'organizations') return user.organizations?.join(' ') || '';
  if (fieldName === 'verifiedEmail') return user.verifiedEmail === true ? 'true' : 'false';
  return user.name || '';
}

function compareUsersBySort(
  left: IUserSearchResult,
  right: IUserSearchResult,
  sort: FieldSortCriterionValue,
) {
  const result = getUserSortValue(left, sort.fieldName).localeCompare(
    getUserSortValue(right, sort.fieldName),
    undefined,
    { sensitivity: 'base' },
  );
  return sort.isAscending ? result : -result;
}

export default function UserManagementPage({ currentUser }: { currentUser?: any }) {
  const [email, setEmail] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [verifyingUserIds, setVerifyingUserIds] = useState<Set<number>>(new Set());
  const [resettingUserIds, setResettingUserIds] = useState<Set<number>>(new Set());
  const [users, setUsers] = useState<IUserSearchResult[]>([]);
  const [sort, setSort] = useState<FieldSortCriterionValue>({
    fieldName: 'name',
    isAscending: true,
  });
  const canVerify =
    currentUser?.internalRoleActions?.includes('WriteInternalManualUserVerification') === true;
  const canResetPassword =
    currentUser?.internalRoleActions?.includes('WriteInternalUserPasswordReset') === true;

  const sortedUsers = useMemo(
    () => [...users].sort((left, right) => compareUsersBySort(left, right, sort)),
    [users, sort.fieldName, sort.isAscending],
  );

  const loadUsers = useCallback(async (
    emailSearch: string,
    sortCriterion: FieldSortCriterionValue,
  ) => {
    if (!emailSearch.length) {
      showToastError('Email search is required.');
      return;
    }

    try {
      setIsSearching(true);
      const response = await searchUsersByEmail({
        email: emailSearch,
        sortField: sortCriterion.fieldName,
        sortAscending: sortCriterion.isAscending,
        page: 0,
        pageSize: 50,
      });
      const nextUsers = (Array.isArray(response) ? response : []).map((user) => ({
        id: Number(user.id),
        email: String(user.email || ''),
        name: String(user.name || ''),
        organizations: Array.isArray(user.organizations) ? user.organizations.map(String) : [],
        verifiedEmail: user.verifiedEmail === true,
      })).filter((user) => Number.isFinite(user.id));

      setUsers(nextUsers);
      setHasSearched(true);
    } catch {
      showToastError('Could not search users.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  const toggleSortDirection = useCallback((fieldName: string) => {
    const nextSort = getNextSortCriterion(sort, fieldName);
    setSort(nextSort);
    const emailSearch = email?.trim() || '';
    if (hasSearched && emailSearch.length) loadUsers(emailSearch, nextSort);
  }, [email, hasSearched, loadUsers, sort]);

  async function searchUsers(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await loadUsers(email?.trim() || '', sort);
  }

  async function verifyUser(user: IUserSearchResult) {
    if (!canVerify || user.verifiedEmail) {
      return;
    }

    setVerifyingUserIds((previous) => new Set(previous).add(user.id));
    try {
      const response = await manuallyVerifyUsersRequest({ userIds: [user.id] });
      if (response?.error) {
        showToastError('Could not verify user.');
        return;
      }
      setUsers((previous) => previous.map((currentUser) => (
        currentUser.id === user.id
          ? { ...currentUser, verifiedEmail: true }
          : currentUser
      )));
      showToastSuccess(`${user.email || 'User'} verified.`);
    } catch {
      showToastError('Could not verify user.');
    } finally {
      setVerifyingUserIds((previous) => {
        const next = new Set(previous);
        next.delete(user.id);
        return next;
      });
    }
  }

  async function sendPasswordReset(user: IUserSearchResult) {
    if (!canResetPassword) return;

    setResettingUserIds((previous) => new Set(previous).add(user.id));
    try {
      const response = await sendUserPasswordResetRequest({ userId: user.id });
      if (response?.error) {
        showToastError('Could not send password reset.');
        return;
      }
      showToastSuccess(`Password reset email sent to ${user.email || 'user'}.`);
    } catch {
      showToastError('Could not send password reset.');
    } finally {
      setResettingUserIds((previous) => {
        const next = new Set(previous);
        next.delete(user.id);
        return next;
      });
    }
  }

  return (
    <div className="stack" data-testid="page-user-management">
      <PageHeader
        title="User Management"
        subtitle={`Internal${canVerify || canResetPassword ? '' : ' (view only)'}`}
      />

      <form className="card filter-card" onSubmit={searchUsers}>
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              placeholder="Search by email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              data-testid="user-management-email-search"
            />
          </div>
          <button
            className="button cta"
            type="submit"
            disabled={isSearching}
            data-testid="user-management-email-submit"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      <div className="card" data-testid="user-management-results">
        <table className="table" data-testid="user-management-results-table">
          <thead>
            <tr>
              <SortableTableHeader label="Name" fieldName="name" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Organizations" fieldName="organizations" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Email" fieldName="email" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Verified Email" fieldName="verifiedEmail" sort={sort} onSort={toggleSortDirection} />
              {canVerify && <th>Verify</th>}
              {canResetPassword && <th>Send Password Reset</th>}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => {
              const isVerifying = verifyingUserIds.has(user.id);
              const isResetting = resettingUserIds.has(user.id);
              return (
                <tr key={user.id} data-testid={`user-management-user-row-${user.id}`}>
                  <td>{user.name || '-'}</td>
                  <td>
                    <OrganizationsCell
                      organizations={user.organizations || []}
                      testIdPrefix={`user-management-user-${user.id}`}
                    />
                  </td>
                  <td>{user.email || '-'}</td>
                  <td>{user.verifiedEmail === true ? 'true' : 'false'}</td>
                  {canVerify && (
                    <td>
                      <button
                        className="button"
                        type="button"
                        disabled={user.verifiedEmail === true || isVerifying}
                        onClick={() => verifyUser(user)}
                        data-testid={`user-management-verify-button-${user.id}`}
                      >
                        {isVerifying ? 'Verifying...' : 'Verify'}
                      </button>
                    </td>
                  )}
                  {canResetPassword && (
                    <td>
                      <button
                        className="button"
                        type="button"
                        disabled={isResetting}
                        onClick={() => sendPasswordReset(user)}
                        data-testid={`user-management-password-reset-button-${user.id}`}
                      >
                        {isResetting ? 'Sending...' : 'Send Password Reset'}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {hasSearched && users.length === 0 && (
              <tr data-testid="user-management-empty-results">
                <td colSpan={4 + Number(canVerify) + Number(canResetPassword)}>No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
